import { cn } from "@/lib/utils";
import { useTranslate } from "ra-core";
import { useRef, type KeyboardEvent } from "react";

import { AGENDA_VIEWS, type AgendaView } from "./agendaModel";

/**
 * Glass segmented control (Día / Semana / Mes). The navy pill slides between
 * equal-width segments with a spring; see `.agenda-switch-indicator` in
 * index.css for the motion and its reduced-motion fallback.
 */
export const AgendaViewSwitch = ({
  value,
  onChange,
}: {
  value: AgendaView;
  onChange: (view: AgendaView) => void;
}) => {
  const translate = useTranslate();
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);
  const index = AGENDA_VIEWS.indexOf(value);

  // Radio-group keyboard model: arrows move and select, Home/End jump.
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const last = AGENDA_VIEWS.length - 1;
    const next =
      event.key === "ArrowRight" || event.key === "ArrowDown"
        ? index === last
          ? 0
          : index + 1
        : event.key === "ArrowLeft" || event.key === "ArrowUp"
          ? index === 0
            ? last
            : index - 1
          : event.key === "Home"
            ? 0
            : event.key === "End"
              ? last
              : null;
    if (next == null) return;
    event.preventDefault();
    onChange(AGENDA_VIEWS[next]);
    buttons.current[next]?.focus();
  };

  return (
    <div
      role="radiogroup"
      aria-label={translate("crm.mobile.agenda_view")}
      onKeyDown={onKeyDown}
      className="agenda-switch liquid-glass relative grid h-10 w-full grid-cols-3 rounded-full p-1 md:w-80"
    >
      <span
        aria-hidden
        className="agenda-switch-indicator pointer-events-none absolute inset-y-1 left-1 agenda-accent rounded-full shadow-sm"
        style={{
          width: `calc((100% - 0.5rem) / ${AGENDA_VIEWS.length})`,
          transform: `translateX(${index * 100}%)`,
        }}
      />
      {AGENDA_VIEWS.map((view, position) => {
        const active = view === value;
        return (
          <button
            key={view}
            ref={(node) => {
              buttons.current[position] = node;
            }}
            type="button"
            role="radio"
            aria-checked={active}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange(view)}
            className={cn(
              "relative z-10 rounded-full text-sm font-medium outline-none transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
              active
                ? "text-[color:var(--agenda-accent-foreground)]"
                : "text-[color:var(--glass-muted)] hover:text-[color:var(--glass-foreground)]",
            )}
          >
            {translate(`crm.mobile.agenda_${view}`)}
          </button>
        );
      })}
    </div>
  );
};
